import { computed, type ComputedRef } from 'vue'
import { useRoute } from 'vue-router'
import { safeStrapiTextField } from '~/utils/iterable-live-merge'

type CatchAllSlug = string | string[] | undefined

/**
 * Same rule as `middleware/page.js`: remove trailing empty catch-all segment (trailing-slash URLs).
 * Without this, e.g. `['page', '']` yields slug `''` and `/api/iterable-live-blocks-meta` returns 400.
 */
function normalizeCatchAllSlugParam (slug: CatchAllSlug): CatchAllSlug {
  if (!Array.isArray(slug)) return slug
  if (slug.length > 1 && slug[slug.length - 1] === '') {
    return slug.slice(0, -1)
  }
  return slug
}

/**
 * Slug passed to Strapi `findOneBySlug` for live Iterable rows — aligned with `middleware/page.js`
 * (trailing empty catch-all stripped above). Root uses CMS slug `home` when the path normalizes to `/` (trailing slashes stripped; aligned with `middleware/route.global.js`).
 * When no usable segment exists on a non-root route, returns `''` so live-meta skips fetch instead of wrong-page merge.
 */
export function useIterableCmsPageSlug (): ComputedRef<string> {
  const route = useRoute()
  return computed(() => {
    const slug = normalizeCatchAllSlugParam(route.params.slug as CatchAllSlug)
    /** Match `middleware/route.global.js` / trailing-slash normalization — not only `path === '/'`. */
    const pathNorm = route.path.replace(/\/+$/, '') || '/'
    if (pathNorm === '/') {
      return 'home'
    }
    if (!slug || (Array.isArray(slug) && slug.length === 0)) {
      return ''
    }
    if (Array.isArray(slug)) {
      /** Deep routes: `getPageBySlug(slug[1])`; shallow: first segment only. Try both when one coerces empty. */
      const candidates = slug.length < 2 ? [slug[0]] : [slug[1], slug[0]]
      for (const seg of candidates) {
        const coerced = safeStrapiTextField(seg, '')
        if (coerced !== '') return coerced
      }
      return ''
    }
    return safeStrapiTextField(slug, '')
  })
}
